import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Heart, ArrowLeft, CheckCircle, Clock, Users, DollarSign, 
  Award, Briefcase, GraduationCap, Plus, X, Linkedin, Globe,
  AlertCircle, Loader2
} from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const EXPERTISE_OPTIONS = [
  'Software Engineering', 'Product Management', 'Data Science', 'UX Design',
  'Marketing', 'Sales', 'Finance', 'HR & Recruiting', 'Operations',
  'Entrepreneurship', 'Leadership', 'Career Transitions', 'Interview Skills',
  'Resume Writing', 'Networking', 'Negotiation'
];

const SPECIALIZATION_OPTIONS = [
  'Mock Interviews', 'Resume Review', 'Career Planning', 'Salary Negotiation',
  'Job Search Strategy', 'LinkedIn Optimization', 'Technical Interviews',
  'Behavioral Interviews', 'Case Studies', 'Portfolio Review', 'Networking Tips',
  'Industry Insights', 'Work-Life Balance', 'Remote Work', 'Startup Advice'
];

export default function BecomeMentor({ user }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    expertise: [],
    years_experience: '',
    current_role: '',
    company: '',
    bio: '',
    motivation: '',
    linkedin_url: '',
    portfolio_url: '',
    hourly_rate: '',
    availability_hours: '',
    specializations: []
  });
  const navigate = useNavigate();

  useEffect(() => {
    checkExistingApplication();
  }, []);

  const checkExistingApplication = async () => {
    try {
      const res = await api.get('/mentor-applications/my');
      if (res.data) {
        setExistingApplication(res.data);
      }
    } catch (error) {
      // No existing application
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleExpertise = (item) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(item)
        ? prev.expertise.filter(e => e !== item)
        : [...prev.expertise, item]
    }));
  };

  const toggleSpecialization = (item) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(item)
        ? prev.specializations.filter(s => s !== item)
        : [...prev.specializations, item]
    }));
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        return formData.expertise.length > 0 && 
               formData.years_experience && 
               formData.current_role && 
               formData.company;
      case 2:
        return formData.bio.length >= 100 && formData.motivation.length >= 50;
      case 3:
        return formData.hourly_rate && formData.availability_hours;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      toast.error('Please complete all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        years_experience: parseInt(formData.years_experience),
        hourly_rate: parseFloat(formData.hourly_rate),
        availability_hours: parseInt(formData.availability_hours)
      };

      await api.post('/mentor-applications', payload);
      toast.success('Application submitted successfully!');
      checkExistingApplication();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  // Show application status if exists
  if (existingApplication) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="bg-white border-b">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft size={20} />
              </Button>
              <h1 className="text-2xl font-heading font-bold">Mentor Application</h1>
            </div>
          </div>
        </header>

        <main className="flex-1 container mx-auto px-6 py-12">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  {existingApplication.status === 'pending' && (
                    <>
                      <Clock className="mx-auto text-yellow-500" size={64} />
                      <h2 className="text-2xl font-bold">Application Under Review</h2>
                      <Badge className={getStatusColor('pending')}>Pending</Badge>
                      <p className="text-muted-foreground">
                        Thank you for applying to become a mentor! Our team is reviewing your application.
                        You&apos;ll receive an email once a decision has been made.
                      </p>
                    </>
                  )}

                  {existingApplication.status === 'approved' && (
                    <>
                      <CheckCircle className="mx-auto text-green-500" size={64} />
                      <h2 className="text-2xl font-bold">🎉 You&apos;re Approved!</h2>
                      <Badge className={getStatusColor('approved')}>Approved</Badge>
                      <p className="text-muted-foreground">
                        Congratulations! You&apos;re now a mentor on The Interview platform.
                        Head over to the Mentorship page to set up your availability.
                      </p>
                      <Button onClick={() => navigate('/mentorship')} className="mt-4">
                        Go to Mentorship Dashboard
                      </Button>
                    </>
                  )}

                  {existingApplication.status === 'rejected' && (
                    <>
                      <AlertCircle className="mx-auto text-red-500" size={64} />
                      <h2 className="text-2xl font-bold">Application Not Approved</h2>
                      <Badge className={getStatusColor('rejected')}>Not Approved</Badge>
                      <p className="text-muted-foreground">
                        Unfortunately, your application wasn&apos;t approved at this time.
                        {existingApplication.admin_notes && (
                          <span className="block mt-2">
                            <strong>Feedback:</strong> {existingApplication.admin_notes}
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        You can reapply in the future with updated credentials.
                      </p>
                    </>
                  )}
                </div>

                {/* Application Summary */}
                <div className="mt-8 pt-6 border-t">
                  <h3 className="font-semibold mb-4">Application Details</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Role:</span>
                      <p className="font-medium">{existingApplication.current_role}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Company:</span>
                      <p className="font-medium">{existingApplication.company}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Experience:</span>
                      <p className="font-medium">{existingApplication.years_experience} years</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Hourly Rate:</span>
                      <p className="font-medium">${existingApplication.hourly_rate}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-muted-foreground">Expertise:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {existingApplication.expertise.map((exp, idx) => (
                          <Badge key={idx} variant="secondary">{exp}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-muted-foreground">Submitted:</span>
                      <p className="font-medium">
                        {new Date(existingApplication.created_at).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-pink-500 to-rose-500 text-white">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white hover:bg-white/20">
              <ArrowLeft size={20} />
            </Button>
            <h1 className="text-2xl font-heading font-bold">Become a Mentor</h1>
          </div>
          <div className="max-w-2xl">
            <p className="text-lg text-pink-100">
              Share your expertise and help shape the careers of aspiring professionals. 
              Join our community of mentors and make a real difference.
            </p>
          </div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-6 py-8">
        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <DollarSign className="mx-auto text-green-500 mb-3" size={32} />
              <h3 className="font-semibold">Earn Extra Income</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Set your own rates and earn 80% of each session
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Users className="mx-auto text-blue-500 mb-3" size={32} />
              <h3 className="font-semibold">Flexible Schedule</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Choose when you&apos;re available for sessions
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Heart className="mx-auto text-pink-500 mb-3" size={32} />
              <h3 className="font-semibold">Make an Impact</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Help candidates succeed in their career journey
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Progress */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">Step {step} of 3</span>
            <span className="text-sm text-muted-foreground">
              {step === 1 ? 'Experience' : step === 2 ? 'About You' : 'Availability & Rates'}
            </span>
          </div>
          <Progress value={(step / 3) * 100} className="h-2" />
        </div>

        {/* Form */}
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              {/* Step 1: Experience */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <CardTitle className="text-lg mb-2 flex items-center gap-2">
                      <Briefcase size={20} /> Professional Background
                    </CardTitle>
                    <CardDescription>Tell us about your professional experience</CardDescription>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="current_role">Current Job Title *</Label>
                      <Input
                        id="current_role"
                        value={formData.current_role}
                        onChange={(e) => handleChange('current_role', e.target.value)}
                        placeholder="e.g., Senior Software Engineer"
                        data-testid="input-current-role"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Company *</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        placeholder="e.g., Google"
                        data-testid="input-company"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="years_experience">Years of Experience *</Label>
                    <Input
                      id="years_experience"
                      type="number"
                      min="1"
                      max="50"
                      value={formData.years_experience}
                      onChange={(e) => handleChange('years_experience', e.target.value)}
                      placeholder="e.g., 8"
                      data-testid="input-years-experience"
                    />
                  </div>

                  <div>
                    <Label>Areas of Expertise * (select at least one)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {EXPERTISE_OPTIONS.map(exp => (
                        <Badge
                          key={exp}
                          variant={formData.expertise.includes(exp) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleExpertise(exp)}
                        >
                          {exp}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="linkedin_url" className="flex items-center gap-2">
                        <Linkedin size={14} /> LinkedIn Profile
                      </Label>
                      <Input
                        id="linkedin_url"
                        value={formData.linkedin_url}
                        onChange={(e) => handleChange('linkedin_url', e.target.value)}
                        placeholder="linkedin.com/in/yourprofile"
                      />
                    </div>
                    <div>
                      <Label htmlFor="portfolio_url" className="flex items-center gap-2">
                        <Globe size={14} /> Portfolio/Website
                      </Label>
                      <Input
                        id="portfolio_url"
                        value={formData.portfolio_url}
                        onChange={(e) => handleChange('portfolio_url', e.target.value)}
                        placeholder="yourwebsite.com"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: About You */}
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <CardTitle className="text-lg mb-2 flex items-center gap-2">
                      <Award size={20} /> About You
                    </CardTitle>
                    <CardDescription>Help candidates understand who you are</CardDescription>
                  </div>

                  <div>
                    <Label htmlFor="bio">Professional Bio * (min 100 characters)</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => handleChange('bio', e.target.value)}
                      placeholder="Share your professional journey, achievements, and what makes you a great mentor..."
                      rows={5}
                      data-testid="input-bio"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.bio.length}/100 characters minimum
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="motivation">Why do you want to become a mentor? * (min 50 characters)</Label>
                    <Textarea
                      id="motivation"
                      value={formData.motivation}
                      onChange={(e) => handleChange('motivation', e.target.value)}
                      placeholder="What motivates you to help others? What do you hope to achieve as a mentor?"
                      rows={4}
                      data-testid="input-motivation"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.motivation.length}/50 characters minimum
                    </p>
                  </div>

                  <div>
                    <Label>What can you help with? (select specializations)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {SPECIALIZATION_OPTIONS.map(spec => (
                        <Badge
                          key={spec}
                          variant={formData.specializations.includes(spec) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleSpecialization(spec)}
                        >
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Availability & Rates */}
              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <CardTitle className="text-lg mb-2 flex items-center gap-2">
                      <DollarSign size={20} /> Availability & Rates
                    </CardTitle>
                    <CardDescription>Set your mentoring preferences</CardDescription>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900">Revenue Split</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      You keep <strong>80%</strong> of each session fee. The Interview takes a 20% platform fee
                      to cover payment processing, marketing, and platform maintenance.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="hourly_rate">Hourly Rate (USD) *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="hourly_rate"
                        type="number"
                        min="25"
                        max="500"
                        value={formData.hourly_rate}
                        onChange={(e) => handleChange('hourly_rate', e.target.value)}
                        placeholder="e.g., 75"
                        className="pl-8"
                        data-testid="input-hourly-rate"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Suggested range: $50 - $150/hour based on experience
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="availability_hours">Hours Available per Week *</Label>
                    <Input
                      id="availability_hours"
                      type="number"
                      min="1"
                      max="40"
                      value={formData.availability_hours}
                      onChange={(e) => handleChange('availability_hours', e.target.value)}
                      placeholder="e.g., 5"
                      data-testid="input-availability-hours"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      How many hours per week can you dedicate to mentoring?
                    </p>
                  </div>

                  {/* Summary */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium mb-3">Application Summary</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Role:</span>
                        <p className="font-medium">{formData.current_role || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Company:</span>
                        <p className="font-medium">{formData.company || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Experience:</span>
                        <p className="font-medium">{formData.years_experience ? `${formData.years_experience} years` : '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Hourly Rate:</span>
                        <p className="font-medium">{formData.hourly_rate ? `$${formData.hourly_rate}` : '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Expertise:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {formData.expertise.map((exp, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{exp}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                {step > 1 ? (
                  <Button variant="outline" onClick={() => setStep(step - 1)}>
                    Back
                  </Button>
                ) : (
                  <div />
                )}

                {step < 3 ? (
                  <Button 
                    onClick={() => setStep(step + 1)} 
                    disabled={!validateStep(step)}
                    data-testid="next-step-btn"
                  >
                    Continue
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    disabled={submitting || !validateStep(3)}
                    className="bg-pink-500 hover:bg-pink-600"
                    data-testid="submit-application-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={16} />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Heart className="mr-2" size={16} />
                        Submit Application
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
